/**
 * useProjectBookmarks - 관심공고(즐겨찾기) 로컬 저장
 *
 * 현재는 브라우저 localStorage 에만 저장한다 (UI 우선, 백엔드 북마크 테이블은 추후).
 * 새로고침해도 유지되지만 이 브라우저에서만 유효하다.
 */

import { useCallback, useEffect, useState } from "react";

const STORAGE_KEY = "vendor:project-bookmarks";

function readInitial(): Set<string> {
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (!raw) return new Set();
    const parsed: unknown = JSON.parse(raw);
    if (Array.isArray(parsed)) {
      return new Set(parsed.filter((v): v is string => typeof v === "string"));
    }
    return new Set();
  } catch {
    return new Set();
  }
}

export interface UseProjectBookmarks {
  /** 북마크된 공고 id 집합 */
  bookmarkedIds: Set<string>;
  /** 북마크 개수 */
  count: number;
  isBookmarked: (projectId: string) => boolean;
  toggle: (projectId: string) => void;
}

export function useProjectBookmarks(): UseProjectBookmarks {
  const [bookmarkedIds, setBookmarkedIds] = useState<Set<string>>(readInitial);

  // 변경 시 localStorage 동기화
  useEffect(() => {
    try {
      window.localStorage.setItem(
        STORAGE_KEY,
        JSON.stringify(Array.from(bookmarkedIds)),
      );
    } catch {
      // 저장 실패는 조용히 무시 (프리뷰/시크릿 모드 등)
    }
  }, [bookmarkedIds]);

  const isBookmarked = useCallback(
    (projectId: string) => bookmarkedIds.has(projectId),
    [bookmarkedIds],
  );

  const toggle = useCallback((projectId: string) => {
    setBookmarkedIds((prev) => {
      const next = new Set(prev);
      if (next.has(projectId)) {
        next.delete(projectId);
      } else {
        next.add(projectId);
      }
      return next;
    });
  }, []);

  return {
    bookmarkedIds,
    count: bookmarkedIds.size,
    isBookmarked,
    toggle,
  };
}
